import {
  createSpecialistLocationGatewayOwnerKey,
  encodeSpecialistLocationGatewayTuple,
  type SpecialistLocationGatewayCanonicalScalar,
} from "./canonicalKey";

export const SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION = 1 as const;
export const SPECIALIST_LOCATION_GATEWAY_PROVIDER_SCHEMA_VERSION = 1 as const;
export const SPECIALIST_LOCATION_GATEWAY_DEFAULT_TTL_MS = 30 * 60 * 1_000;
export const SPECIALIST_LOCATION_GATEWAY_MAX_TTL_MS = 24 * 60 * 60 * 1_000;
export const SPECIALIST_LOCATION_GATEWAY_MAX_CLOCK_SKEW_MS = 5 * 60 * 1_000;

const MAX_IDENTIFIER_LENGTH = 160;
const MAX_HREF_LENGTH = 2_048;
const MAX_LOCALE_LENGTH = 32;
const MAX_PUBLICATION_ROWS = 50;
const MAX_OWNER_KEY_LENGTH = 20_000;
const FINGERPRINT_PREFIX = "specialist-location-gateway-fingerprint-v1:";

export type SpecialistLocationGatewayDestinationKind =
  | "group"
  | "individual"
  | "package";

export type SpecialistLocationGatewaySourceAction =
  | "package-details-add"
  | "package-reserve"
  | "portfolio-book-now"
  | "primary-booking"
  | "results-service-bundle-reserve"
  | "results-service-reserve"
  | "service-details-add"
  | "service-bundle-reserve"
  | "service-reserve";

export type SpecialistLocationGatewayIntentV1 =
  | { kind: "appointment-unscoped" }
  | {
      kind: "service";
      globalServiceRef: string;
      sourceOfferingId: string;
    }
  | {
      kind: "service-bundle";
      globalBundleRef: string;
      sourceOfferingId: string;
    }
  | {
      kind: "package-purchase";
      globalPackageRef: string;
      sourcePackageId: string;
    }
  | {
      kind: "catalog";
      initialCatalog: "packages" | "services";
    };

export interface SpecialistLocationGatewaySourceV1 {
  personId: string;
  sourceLocationId: string;
  sourceUserLocationId: string;
  canonicalReturnHref: string;
  sourceProfileRevision: string;
  sourceCatalogVersion: string;
}

export interface SpecialistLocationGatewayIntentRecordV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION;
  kind: "specialist-location-gateway-intent";
  draftId: string;
  ownerKey: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  createdAtEpochMs: number;
  expiresAtEpochMs: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
}

export interface SpecialistLocationGatewayLocationPresentationV1 {
  name: string;
  address: string;
  city: string;
  state: string;
  countryCode: string;
  timeZone: string;
  coordinates: { latitude: number; longitude: number } | null;
  category: string;
  imageSrc: string | null;
  imageAlt: string;
  images: readonly {
    id: string;
    src: string;
    alt: string;
    width?: number;
    height?: number;
  }[];
  rating: number | null;
  reviewCount: number;
  presentationRevision: string;
}

export interface SpecialistLocationGatewayCatalogReferenceV1 {
  locationId: string;
  providerId: string;
  revision: string;
  catalogVersion: string;
  fingerprint: string;
  catalogOwnerUserLocationId: string;
}

export interface SpecialistLocationGatewayServiceMappingV1 {
  kind: "service" | "service-bundle";
  globalRef: string;
  sourceOfferingId: string;
  destinationOfferingId: string;
  destinationServiceId: string;
  destinationLocationId: string;
  destinationUserLocationId: string;
  destinationCatalogVersion: string;
}

export interface SpecialistLocationGatewayPackageMappingV1 {
  kind: "package";
  globalRef: string;
  sourcePackageId: string;
  destinationPackageId: string;
  destinationLocationId: string;
  destinationUserLocationId: string;
  destinationCatalogVersion: string;
  destinationPackageVersion: string;
  publicationProviderId: string;
}

export interface SpecialistLocationGatewayAppointmentAuthorityV1 {
  directBookingEligible: true;
  calendarBookingsEnabled: true;
  onlineBookableOfferingCount: number;
  organizerCatalog: SpecialistLocationGatewayCatalogReferenceV1;
  locationCatalog: SpecialistLocationGatewayCatalogReferenceV1;
  mappedOffering: SpecialistLocationGatewayServiceMappingV1 | null;
  capabilities: {
    individual: boolean;
    group: boolean;
    groupAggregateActivationRevision: string | null;
  };
}

export interface SpecialistLocationGatewayPackageAuthorityV1 {
  assignmentAuthorizedPackageCount: number;
  packageCatalogLocationId: string;
  packageCatalogOwnerUserLocationId: string;
  packageCatalogProviderId: string;
  packageCatalogRevision: string;
  packageCatalogVersion: string;
  packageCatalogFingerprint: string;
  mappedPackage: SpecialistLocationGatewayPackageMappingV1 | null;
}

export interface SpecialistLocationGatewayEligibilityRowV1 {
  order: number;
  personId: string;
  locationId: string;
  userLocationId: string;
  assignmentProfileRevision: string;
  standaloneProfilePublished: true;
  capabilityRevision: string;
  presentation: SpecialistLocationGatewayLocationPresentationV1;
  appointment: SpecialistLocationGatewayAppointmentAuthorityV1 | null;
  packagePurchase: SpecialistLocationGatewayPackageAuthorityV1 | null;
}

export interface SpecialistLocationGatewayPublicationV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_PROVIDER_SCHEMA_VERSION;
  kind: "specialist-location-gateway-publication";
  providerId: string;
  providerRevision: string;
  intentDraftId: string;
  intentOwnerKey: string;
  sourceProfileRevision: string;
  sourceCatalogVersion: string;
  personId: string;
  sourceLocationId: string;
  sourceUserLocationId: string;
  locale: string;
  generation: number;
  publishedAtEpochMs: number;
  expiresAtEpochMs: number;
  rows: readonly SpecialistLocationGatewayEligibilityRowV1[];
}

export type SpecialistLocationGatewaySelectedAuthorityReferenceV1 =
  | {
      kind: "appointment";
      organizerCatalog: SpecialistLocationGatewayCatalogReferenceV1;
      locationCatalog: SpecialistLocationGatewayCatalogReferenceV1;
      mappedOffering: SpecialistLocationGatewayServiceMappingV1 | null;
      individualCapability: boolean;
      groupCapability: boolean;
      groupAggregateActivationRevision: string | null;
    }
  | {
      kind: "package";
      packageCatalogLocationId: string;
      packageCatalogOwnerUserLocationId: string;
      packageCatalogProviderId: string;
      packageCatalogRevision: string;
      packageCatalogVersion: string;
      packageCatalogFingerprint: string;
      mappedPackage: SpecialistLocationGatewayPackageMappingV1 | null;
    };

export interface SpecialistLocationGatewayFrozenSelectionV1 {
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  gatewayCapabilityRevision: string;
  selectedLocationId: string;
  selectedUserLocationId: string;
  selectedAssignmentProfileRevision: string;
  authority: SpecialistLocationGatewaySelectedAuthorityReferenceV1;
}

interface SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION;
  draftId: string;
  ownerKey: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  intentDraftId: string;
  createdAtEpochMs: number;
  expiresAtEpochMs: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
  selection: SpecialistLocationGatewayFrozenSelectionV1;
}

export interface SpecialistLocationGatewayPreDraftResolutionV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-pre-draft-resolution";
  mutationRevision: 0;
}

export interface SpecialistLocationGatewayNavigationCommitClaimV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-navigation-commit-claim";
  mutationRevision: 1;
  attemptId: string;
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  claimedAtEpochMs: number;
}

export interface SpecialistLocationGatewayCommittedNavigationLineageV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-navigation-lineage";
  mutationRevision: 2;
  resolutionId: string;
  attemptId: string;
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  committedAtEpochMs: number;
}

export interface SpecialistLocationGatewayCatalogTransitionClaimV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-catalog-transition-claim";
  mutationRevision: 3;
  attemptId: string;
  targetCatalog: "packages" | "services";
  targetGeneration: number;
  claimedAtEpochMs: number;
}

export interface SpecialistLocationGatewayAuthRebindClaimV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-auth-rebind-claim";
  mutationRevision: 4;
  attemptId: string;
  authenticatedCustomerAccountId: string;
  targetGeneration: number;
  targetCreatedAtEpochMs: number;
  targetExpiresAtEpochMs: number;
  claimedAtEpochMs: number;
}

export interface SpecialistLocationGatewayDestinationLifecycleClaimV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-destination-lifecycle-claim";
  mutationRevision: 5;
  resolutionId: string;
  lineageAttemptId: string;
  lifecycleAttemptId: string;
  action: "close" | "return-to-pre-draft";
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  mutableCleanupFingerprint: string;
  lineageCommittedAtEpochMs: number;
  claimedAtEpochMs: number;
}

export interface SpecialistLocationGatewayCommittedAuthRebindClaimV1
  extends SpecialistLocationGatewayNavigationAuthorityBaseV1 {
  kind: "specialist-location-gateway-committed-auth-rebind-claim";
  mutationRevision: 6;
  resolutionId: string;
  lineageAttemptId: string;
  authAttemptId: string;
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  priorDestinationDraftFingerprint: string;
  authenticatedDestinationDraftFingerprint: string;
  destinationOwnerBindingMode: "companion-only" | "durable-owner";
  authenticatedCustomerAccountId: string;
  targetGeneration: number;
  targetCreatedAtEpochMs: number;
  targetExpiresAtEpochMs: number;
  lineageCommittedAtEpochMs: number;
  claimedAtEpochMs: number;
}

/**
 * Persistent serialization claim for bounded-store capacity reclamation.
 * An orphan intent has no selected Location authority, so this claim binds the
 * exact intent owner directly instead of fabricating a frozen selection. When
 * a pre-draft resolution existed, its complete snapshot is retained for exact
 * compensation until the paired intent removal has been proven durable.
 */
export interface SpecialistLocationGatewayPreDraftGcClaimV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION;
  kind: "specialist-location-gateway-pre-draft-gc-claim";
  mutationRevision: 7;
  draftId: string;
  ownerKey: string;
  intentOwnerKey: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  intentDraftId: string;
  createdAtEpochMs: number;
  expiresAtEpochMs: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
  priorResolution: SpecialistLocationGatewayPreDraftResolutionV1 | null;
  claimedAtEpochMs: number;
}

export type SpecialistLocationGatewayNavigationAuthorityV1 =
  | SpecialistLocationGatewayAuthRebindClaimV1
  | SpecialistLocationGatewayCommittedAuthRebindClaimV1
  | SpecialistLocationGatewayCommittedNavigationLineageV1
  | SpecialistLocationGatewayDestinationLifecycleClaimV1
  | SpecialistLocationGatewayNavigationCommitClaimV1
  | SpecialistLocationGatewayCatalogTransitionClaimV1
  | SpecialistLocationGatewayPreDraftGcClaimV1
  | SpecialistLocationGatewayPreDraftResolutionV1;

export type SpecialistLocationGatewayValidation<T> =
  | { valid: true; draft: T }
  | { valid: false; reason: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[],
) {
  const keys = Object.keys(value);
  return (
    keys.length === expectedKeys.length &&
    keys.every((key) => expectedKeys.includes(key))
  );
}

export function isSpecialistLocationGatewayIdentifier(
  value: unknown,
): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= MAX_IDENTIFIER_LENGTH
  );
}

export function createSpecialistLocationGatewayFingerprint(
  fields: readonly SpecialistLocationGatewayCanonicalScalar[],
) {
  return `${FINGERPRINT_PREFIX}${encodeSpecialistLocationGatewayTuple(fields)}`;
}

export function isSpecialistLocationGatewayFingerprint(
  value: unknown,
): value is string {
  if (
    typeof value !== "string" ||
    !value.startsWith(FINGERPRINT_PREFIX) ||
    value.length > MAX_OWNER_KEY_LENGTH
  ) {
    return false;
  }
  const encoded = value.slice(FINGERPRINT_PREFIX.length);
  try {
    const parsed = JSON.parse(encoded) as unknown;
    return (
      Array.isArray(parsed) &&
      parsed.every(
        (field) =>
          field === null ||
          typeof field === "string" ||
          typeof field === "boolean" ||
          (typeof field === "number" && Number.isSafeInteger(field)),
      ) &&
      JSON.stringify(parsed) === encoded
    );
  } catch {
    return false;
  }
}

function isSafeLocalHref(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > MAX_HREF_LENGTH ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    /[\u0000-\u001f\u007f]/u.test(value)
  ) {
    return false;
  }

  try {
    const origin = "https://widget.volera.invalid";
    const url = new URL(value, origin);
    return (
      url.origin === origin &&
      url.username === "" &&
      url.password === "" &&
      url.hash === "" &&
      `${url.pathname}${url.search}` === value
    );
  } catch {
    return false;
  }
}

function isEpoch(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0
  );
}

function cloneSource(value: unknown): SpecialistLocationGatewaySourceV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "personId",
      "sourceLocationId",
      "sourceUserLocationId",
      "canonicalReturnHref",
      "sourceProfileRevision",
      "sourceCatalogVersion",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.personId) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceUserLocationId) ||
    !isSafeLocalHref(value.canonicalReturnHref) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceProfileRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceCatalogVersion) ||
    value.canonicalReturnHref !==
      `/locations/${encodeURIComponent(
        value.sourceLocationId,
      )}/specialists/${encodeURIComponent(value.sourceUserLocationId)}`
  ) {
    return null;
  }

  return {
    personId: value.personId,
    sourceLocationId: value.sourceLocationId,
    sourceUserLocationId: value.sourceUserLocationId,
    canonicalReturnHref: value.canonicalReturnHref,
    sourceProfileRevision: value.sourceProfileRevision,
    sourceCatalogVersion: value.sourceCatalogVersion,
  };
}

function cloneIntent(value: unknown): SpecialistLocationGatewayIntentV1 | null {
  if (!isRecord(value)) return null;

  if (value.kind === "appointment-unscoped") {
    return Object.keys(value).length === 1 ? { kind: value.kind } : null;
  }

  if (
    value.kind === "service" &&
    hasExactKeys(value, ["kind", "globalServiceRef", "sourceOfferingId"]) &&
    isSpecialistLocationGatewayIdentifier(value.globalServiceRef) &&
    isSpecialistLocationGatewayIdentifier(value.sourceOfferingId)
  ) {
    return {
      kind: value.kind,
      globalServiceRef: value.globalServiceRef,
      sourceOfferingId: value.sourceOfferingId,
    };
  }

  if (
    value.kind === "service-bundle" &&
    hasExactKeys(value, ["kind", "globalBundleRef", "sourceOfferingId"]) &&
    isSpecialistLocationGatewayIdentifier(value.globalBundleRef) &&
    isSpecialistLocationGatewayIdentifier(value.sourceOfferingId)
  ) {
    return {
      kind: value.kind,
      globalBundleRef: value.globalBundleRef,
      sourceOfferingId: value.sourceOfferingId,
    };
  }

  if (
    value.kind === "package-purchase" &&
    hasExactKeys(value, ["kind", "globalPackageRef", "sourcePackageId"]) &&
    isSpecialistLocationGatewayIdentifier(value.globalPackageRef) &&
    isSpecialistLocationGatewayIdentifier(value.sourcePackageId)
  ) {
    return {
      kind: value.kind,
      globalPackageRef: value.globalPackageRef,
      sourcePackageId: value.sourcePackageId,
    };
  }

  if (
    value.kind === "catalog" &&
    hasExactKeys(value, ["kind", "initialCatalog"]) &&
    (value.initialCatalog === "services" || value.initialCatalog === "packages")
  ) {
    return { kind: value.kind, initialCatalog: value.initialCatalog };
  }

  return null;
}

function isSourceAction(
  value: unknown,
): value is SpecialistLocationGatewaySourceAction {
  return (
    value === "package-details-add" ||
    value === "package-reserve" ||
    value === "portfolio-book-now" ||
    value === "primary-booking" ||
    value === "results-service-bundle-reserve" ||
    value === "results-service-reserve" ||
    value === "service-details-add" ||
    value === "service-bundle-reserve" ||
    value === "service-reserve"
  );
}

export function isResultsSpecialistLocationGatewaySourceAction(
  value: SpecialistLocationGatewaySourceAction,
) {
  return (
    value === "results-service-reserve" ||
    value === "results-service-bundle-reserve"
  );
}

export function isSpecialistLocationGatewayInitialSourceActionIntentPair(
  sourceAction: SpecialistLocationGatewaySourceAction,
  intent: SpecialistLocationGatewayIntentV1,
) {
  if (!intent || typeof intent !== "object") return false;
  if (sourceAction === "package-details-add" || sourceAction === "package-reserve") {
    return intent.kind === "package-purchase";
  }
  if (
    sourceAction === "service-bundle-reserve" ||
    sourceAction === "results-service-bundle-reserve"
  ) {
    return intent.kind === "service-bundle";
  }
  if (
    sourceAction === "service-reserve" ||
    sourceAction === "results-service-reserve"
  ) {
    return intent.kind === "service";
  }
  if (sourceAction === "service-details-add") {
    return intent.kind === "service" || intent.kind === "service-bundle";
  }
  if (sourceAction === "portfolio-book-now") {
    return (
      intent.kind === "appointment-unscoped" ||
      intent.kind === "service" ||
      intent.kind === "service-bundle" ||
      (intent.kind === "catalog" && intent.initialCatalog === "services")
    );
  }
  return intent.kind === "appointment-unscoped" || intent.kind === "catalog";
}

function sourceActionMatchesIntent(
  sourceAction: SpecialistLocationGatewaySourceAction,
  intent: SpecialistLocationGatewayIntentV1,
) {
  // A store-owned catalog transition preserves the immutable Profile action
  // that opened SLG while replacing the mutable final catalog intent.
  return (
    intent.kind === "catalog" ||
    isSpecialistLocationGatewayInitialSourceActionIntentPair(
      sourceAction,
      intent,
    )
  );
}

function hasValidLifetime(value: Record<string, unknown>) {
  return (
    isEpoch(value.createdAtEpochMs) &&
    isEpoch(value.expiresAtEpochMs) &&
    value.expiresAtEpochMs > value.createdAtEpochMs &&
    value.expiresAtEpochMs - value.createdAtEpochMs <=
      SPECIALIST_LOCATION_GATEWAY_MAX_TTL_MS
  );
}

function isLocale(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= MAX_LOCALE_LENGTH &&
    /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/u.test(value)
  );
}

function sourceOwnerFields(
  source: SpecialistLocationGatewaySourceV1,
): (string | number | boolean | null)[] {
  return [
    source.personId,
    source.sourceLocationId,
    source.sourceUserLocationId,
    source.canonicalReturnHref,
    source.sourceProfileRevision,
    source.sourceCatalogVersion,
  ];
}

function intentOwnerFields(
  intent: SpecialistLocationGatewayIntentV1,
): (string | number | boolean | null)[] {
  switch (intent.kind) {
    case "appointment-unscoped":
      return [intent.kind];
    case "service":
      return [intent.kind, intent.globalServiceRef, intent.sourceOfferingId];
    case "service-bundle":
      return [intent.kind, intent.globalBundleRef, intent.sourceOfferingId];
    case "package-purchase":
      return [intent.kind, intent.globalPackageRef, intent.sourcePackageId];
    case "catalog":
      return [intent.kind, intent.initialCatalog];
  }
}

function catalogReferenceOwnerFields(
  reference: SpecialistLocationGatewayCatalogReferenceV1,
): (string | number | boolean | null)[] {
  return [
    reference.locationId,
    reference.providerId,
    reference.revision,
    reference.catalogVersion,
    reference.fingerprint,
    reference.catalogOwnerUserLocationId,
  ];
}

function serviceMappingOwnerFields(
  mapping: SpecialistLocationGatewayServiceMappingV1 | null,
): (string | number | boolean | null)[] {
  return mapping
    ? [
        true,
        mapping.kind,
        mapping.globalRef,
        mapping.sourceOfferingId,
        mapping.destinationOfferingId,
        mapping.destinationServiceId,
        mapping.destinationLocationId,
        mapping.destinationUserLocationId,
        mapping.destinationCatalogVersion,
      ]
    : [false];
}

function packageMappingOwnerFields(
  mapping: SpecialistLocationGatewayPackageMappingV1 | null,
): (string | number | boolean | null)[] {
  return mapping
    ? [
        true,
        mapping.kind,
        mapping.globalRef,
        mapping.sourcePackageId,
        mapping.destinationPackageId,
        mapping.destinationLocationId,
        mapping.destinationUserLocationId,
        mapping.destinationCatalogVersion,
        mapping.destinationPackageVersion,
        mapping.publicationProviderId,
      ]
    : [false];
}

function selectionOwnerFields(
  selection: SpecialistLocationGatewayFrozenSelectionV1,
): (string | number | boolean | null)[] {
  const common = [
    selection.gatewayProviderId,
    selection.gatewayProviderRevision,
    selection.gatewayCapabilityRevision,
    selection.selectedLocationId,
    selection.selectedUserLocationId,
    selection.selectedAssignmentProfileRevision,
    selection.authority.kind,
  ];

  if (selection.authority.kind === "appointment") {
    return [
      ...common,
      ...catalogReferenceOwnerFields(selection.authority.organizerCatalog),
      ...catalogReferenceOwnerFields(selection.authority.locationCatalog),
      ...serviceMappingOwnerFields(selection.authority.mappedOffering),
      selection.authority.individualCapability,
      selection.authority.groupCapability,
      selection.authority.groupAggregateActivationRevision,
    ];
  }

  return [
    ...common,
    selection.authority.packageCatalogLocationId,
    selection.authority.packageCatalogOwnerUserLocationId,
    selection.authority.packageCatalogProviderId,
    selection.authority.packageCatalogRevision,
    selection.authority.packageCatalogVersion,
    selection.authority.packageCatalogFingerprint,
    ...packageMappingOwnerFields(selection.authority.mappedPackage),
  ];
}

export function createSpecialistLocationGatewayIntentOwnerKey(input: {
  draftId: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  createdAtEpochMs: number;
  expiresAtEpochMs: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
}) {
  return createSpecialistLocationGatewayOwnerKey({
    stage: "intent-v1",
    fields: [
      input.draftId,
      input.gatewayProviderId,
      input.gatewayProviderRevision,
      input.customerAccountId,
      input.locale,
      input.generation,
      input.createdAtEpochMs,
      input.expiresAtEpochMs,
      input.sourceAction,
      ...sourceOwnerFields(input.source),
      ...intentOwnerFields(input.intent),
    ],
  });
}

export function createSpecialistLocationGatewayIntentRecord(input: {
  draftId: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  createdAtEpochMs: number;
  expiresAtEpochMs?: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
}): SpecialistLocationGatewayIntentRecordV1 {
  const body = {
    draftId: input.draftId,
    gatewayProviderId: input.gatewayProviderId,
    gatewayProviderRevision: input.gatewayProviderRevision,
    customerAccountId: input.customerAccountId,
    locale: input.locale,
    generation: input.generation,
    createdAtEpochMs: input.createdAtEpochMs,
    expiresAtEpochMs:
      input.expiresAtEpochMs ??
      input.createdAtEpochMs + SPECIALIST_LOCATION_GATEWAY_DEFAULT_TTL_MS,
    sourceAction: input.sourceAction,
    source: input.source,
    intent: input.intent,
  };

  return {
    schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
    kind: "specialist-location-gateway-intent",
    ...body,
    ownerKey: createSpecialistLocationGatewayIntentOwnerKey(body),
  };
}

export function validateSpecialistLocationGatewayIntentRecord(
  value: unknown,
): SpecialistLocationGatewayValidation<SpecialistLocationGatewayIntentRecordV1> {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "draftId",
      "ownerKey",
      "gatewayProviderId",
      "gatewayProviderRevision",
      "customerAccountId",
      "locale",
      "generation",
      "createdAtEpochMs",
      "expiresAtEpochMs",
      "sourceAction",
      "source",
      "intent",
    ]) ||
    value.schemaVersion !== SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION ||
    value.kind !== "specialist-location-gateway-intent" ||
    !isSpecialistLocationGatewayIdentifier(value.draftId) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayProviderId) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayProviderRevision) ||
    typeof value.ownerKey !== "string" ||
    value.ownerKey.length === 0 ||
    value.ownerKey.length > MAX_OWNER_KEY_LENGTH ||
    (value.customerAccountId !== null &&
      !isSpecialistLocationGatewayIdentifier(value.customerAccountId)) ||
    !isLocale(value.locale) ||
    !Number.isSafeInteger(value.generation) ||
    Number(value.generation) < 0 ||
    !hasValidLifetime(value) ||
    !isSourceAction(value.sourceAction)
  ) {
    return { valid: false, reason: "invalid-specialist-location-gateway-intent" };
  }

  const source = cloneSource(value.source);
  const intent = cloneIntent(value.intent);
  if (!source || !intent || !sourceActionMatchesIntent(value.sourceAction, intent)) {
    return { valid: false, reason: "invalid-specialist-location-gateway-intent" };
  }

  const body = {
    draftId: value.draftId,
    gatewayProviderId: value.gatewayProviderId,
    gatewayProviderRevision: value.gatewayProviderRevision,
    customerAccountId: value.customerAccountId as string | null,
    locale: value.locale,
    generation: value.generation as number,
    createdAtEpochMs: value.createdAtEpochMs as number,
    expiresAtEpochMs: value.expiresAtEpochMs as number,
    sourceAction: value.sourceAction,
    source,
    intent,
  };

  if (value.ownerKey !== createSpecialistLocationGatewayIntentOwnerKey(body)) {
    return { valid: false, reason: "specialist-location-gateway-intent-owner-mismatch" };
  }

  return {
    valid: true,
    draft: {
      schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
      kind: "specialist-location-gateway-intent",
      ...body,
      ownerKey: value.ownerKey,
    },
  };
}

export function isSpecialistLocationGatewayRecordFresh(
  value: { createdAtEpochMs: number; expiresAtEpochMs: number },
  nowEpochMs: number,
) {
  return (
    Number.isSafeInteger(nowEpochMs) &&
    nowEpochMs >= 0 &&
    value.createdAtEpochMs <=
      nowEpochMs + SPECIALIST_LOCATION_GATEWAY_MAX_CLOCK_SKEW_MS &&
    value.expiresAtEpochMs > nowEpochMs
  );
}

function cloneCatalogReference(
  value: unknown,
): SpecialistLocationGatewayCatalogReferenceV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "locationId",
      "providerId",
      "revision",
      "catalogVersion",
      "fingerprint",
      "catalogOwnerUserLocationId",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.locationId) ||
    !isSpecialistLocationGatewayIdentifier(value.providerId) ||
    !isSpecialistLocationGatewayIdentifier(value.revision) ||
    !isSpecialistLocationGatewayIdentifier(value.catalogVersion) ||
    !isSpecialistLocationGatewayFingerprint(value.fingerprint) ||
    !isSpecialistLocationGatewayIdentifier(value.catalogOwnerUserLocationId)
  ) {
    return null;
  }
  return {
    locationId: value.locationId,
    providerId: value.providerId,
    revision: value.revision,
    catalogVersion: value.catalogVersion,
    fingerprint: value.fingerprint,
    catalogOwnerUserLocationId: value.catalogOwnerUserLocationId,
  };
}

function cloneServiceMapping(
  value: unknown,
): SpecialistLocationGatewayServiceMappingV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "globalRef",
      "sourceOfferingId",
      "destinationOfferingId",
      "destinationServiceId",
      "destinationLocationId",
      "destinationUserLocationId",
      "destinationCatalogVersion",
    ]) ||
    (value.kind !== "service" && value.kind !== "service-bundle") ||
    !isSpecialistLocationGatewayIdentifier(value.globalRef) ||
    !isSpecialistLocationGatewayIdentifier(value.sourceOfferingId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationOfferingId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationServiceId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationUserLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationCatalogVersion)
  ) {
    return null;
  }
  return {
    kind: value.kind,
    globalRef: value.globalRef,
    sourceOfferingId: value.sourceOfferingId,
    destinationOfferingId: value.destinationOfferingId,
    destinationServiceId: value.destinationServiceId,
    destinationLocationId: value.destinationLocationId,
    destinationUserLocationId: value.destinationUserLocationId,
    destinationCatalogVersion: value.destinationCatalogVersion,
  };
}

function clonePackageMapping(
  value: unknown,
): SpecialistLocationGatewayPackageMappingV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "globalRef",
      "sourcePackageId",
      "destinationPackageId",
      "destinationLocationId",
      "destinationUserLocationId",
      "destinationCatalogVersion",
      "destinationPackageVersion",
      "publicationProviderId",
    ]) ||
    value.kind !== "package" ||
    !isSpecialistLocationGatewayIdentifier(value.globalRef) ||
    !isSpecialistLocationGatewayIdentifier(value.sourcePackageId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationPackageId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationUserLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationCatalogVersion) ||
    !isSpecialistLocationGatewayIdentifier(value.destinationPackageVersion) ||
    !isSpecialistLocationGatewayIdentifier(value.publicationProviderId)
  ) {
    return null;
  }
  return {
    kind: "package",
    globalRef: value.globalRef,
    sourcePackageId: value.sourcePackageId,
    destinationPackageId: value.destinationPackageId,
    destinationLocationId: value.destinationLocationId,
    destinationUserLocationId: value.destinationUserLocationId,
    destinationCatalogVersion: value.destinationCatalogVersion,
    destinationPackageVersion: value.destinationPackageVersion,
    publicationProviderId: value.publicationProviderId,
  };
}

function cloneSelectedAuthorityReference(
  value: unknown,
): SpecialistLocationGatewaySelectedAuthorityReferenceV1 | null {
  if (!isRecord(value)) return null;

  if (value.kind === "appointment") {
    if (
      !hasExactKeys(value, [
        "kind",
        "organizerCatalog",
        "locationCatalog",
        "mappedOffering",
        "individualCapability",
        "groupCapability",
        "groupAggregateActivationRevision",
      ])
    ) {
      return null;
    }
    const organizerCatalog = cloneCatalogReference(value.organizerCatalog);
    const locationCatalog = cloneCatalogReference(value.locationCatalog);
    const mappedOffering =
      value.mappedOffering === null
        ? null
        : cloneServiceMapping(value.mappedOffering);
    if (
      !organizerCatalog ||
      !locationCatalog ||
      mappedOffering === null && value.mappedOffering !== null ||
      typeof value.individualCapability !== "boolean" ||
      typeof value.groupCapability !== "boolean" ||
      (value.groupAggregateActivationRevision !== null &&
        !isSpecialistLocationGatewayIdentifier(
          value.groupAggregateActivationRevision,
        )) ||
      (value.groupCapability &&
        value.groupAggregateActivationRevision === null)
    ) {
      return null;
    }
    return {
      kind: value.kind,
      organizerCatalog,
      locationCatalog,
      mappedOffering,
      individualCapability: value.individualCapability,
      groupCapability: value.groupCapability,
      groupAggregateActivationRevision:
        value.groupAggregateActivationRevision as string | null,
    };
  }

  if (value.kind === "package") {
    if (
      !hasExactKeys(value, [
        "kind",
        "packageCatalogLocationId",
        "packageCatalogOwnerUserLocationId",
        "packageCatalogProviderId",
        "packageCatalogRevision",
        "packageCatalogVersion",
        "packageCatalogFingerprint",
        "mappedPackage",
      ])
    ) {
      return null;
    }
    const mappedPackage =
      value.mappedPackage === null
        ? null
        : clonePackageMapping(value.mappedPackage);
    if (
      !isSpecialistLocationGatewayIdentifier(value.packageCatalogLocationId) ||
      !isSpecialistLocationGatewayIdentifier(
        value.packageCatalogOwnerUserLocationId,
      ) ||
      !isSpecialistLocationGatewayIdentifier(value.packageCatalogProviderId) ||
      !isSpecialistLocationGatewayIdentifier(value.packageCatalogRevision) ||
      !isSpecialistLocationGatewayIdentifier(value.packageCatalogVersion) ||
      !isSpecialistLocationGatewayFingerprint(
        value.packageCatalogFingerprint,
      ) ||
      mappedPackage === null && value.mappedPackage !== null
    ) {
      return null;
    }
    return {
      kind: value.kind,
      packageCatalogLocationId: value.packageCatalogLocationId,
      packageCatalogOwnerUserLocationId:
        value.packageCatalogOwnerUserLocationId,
      packageCatalogProviderId: value.packageCatalogProviderId,
      packageCatalogRevision: value.packageCatalogRevision,
      packageCatalogVersion: value.packageCatalogVersion,
      packageCatalogFingerprint: value.packageCatalogFingerprint,
      mappedPackage,
    };
  }

  return null;
}

function cloneFrozenSelection(
  value: unknown,
): SpecialistLocationGatewayFrozenSelectionV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "gatewayProviderId",
      "gatewayProviderRevision",
      "gatewayCapabilityRevision",
      "selectedLocationId",
      "selectedUserLocationId",
      "selectedAssignmentProfileRevision",
      "authority",
    ]) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayProviderId) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayProviderRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayCapabilityRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.selectedLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.selectedUserLocationId) ||
    !isSpecialistLocationGatewayIdentifier(
      value.selectedAssignmentProfileRevision,
    )
  ) {
    return null;
  }
  const authority = cloneSelectedAuthorityReference(value.authority);
  return authority
    ? {
        gatewayProviderId: value.gatewayProviderId,
        gatewayProviderRevision: value.gatewayProviderRevision,
        gatewayCapabilityRevision: value.gatewayCapabilityRevision,
        selectedLocationId: value.selectedLocationId,
        selectedUserLocationId: value.selectedUserLocationId,
        selectedAssignmentProfileRevision:
          value.selectedAssignmentProfileRevision,
        authority,
      }
    : null;
}

function isCanonicalCopy(value: unknown, maximum = 5_000): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= maximum &&
    !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(value)
  );
}

function isIanaTimeZone(value: unknown): value is string {
  if (!isSpecialistLocationGatewayIdentifier(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

function isSafePublicMediaUrl(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length === 0 ||
    value.length > 2_048 ||
    /[\u0000-\u001f\u007f]/u.test(value)
  ) {
    return false;
  }
  if (value.startsWith("/") && !value.startsWith("//") && !value.includes("\\")) {
    return true;
  }
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.username === "" && url.password === "";
  } catch {
    return false;
  }
}

function cloneLocationPresentation(
  value: unknown,
): SpecialistLocationGatewayLocationPresentationV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "name",
      "address",
      "city",
      "state",
      "countryCode",
      "timeZone",
      "coordinates",
      "category",
      "imageSrc",
      "imageAlt",
      "images",
      "rating",
      "reviewCount",
      "presentationRevision",
    ]) ||
    !isCanonicalCopy(value.name) ||
    !isCanonicalCopy(value.address) ||
    !isCanonicalCopy(value.city) ||
    !isCanonicalCopy(value.state) ||
    typeof value.countryCode !== "string" ||
    !/^[A-Z]{2}$/u.test(value.countryCode) ||
    !isIanaTimeZone(value.timeZone) ||
    !isCanonicalCopy(value.category) ||
    (value.imageSrc !== null && !isSafePublicMediaUrl(value.imageSrc)) ||
    !isCanonicalCopy(value.imageAlt) ||
    !Array.isArray(value.images) ||
    value.images.length > 20 ||
    (value.rating !== null &&
      (typeof value.rating !== "number" ||
        !Number.isFinite(value.rating) ||
        value.rating < 0 ||
        value.rating > 5)) ||
    !Number.isSafeInteger(value.reviewCount) ||
    Number(value.reviewCount) < 0 ||
    !isSpecialistLocationGatewayIdentifier(value.presentationRevision)
  ) {
    return null;
  }
  let coordinates: { latitude: number; longitude: number } | null = null;
  if (value.coordinates !== null) {
    if (
      !isRecord(value.coordinates) ||
      !hasExactKeys(value.coordinates, ["latitude", "longitude"]) ||
      typeof value.coordinates.latitude !== "number" ||
      !Number.isFinite(value.coordinates.latitude) ||
      value.coordinates.latitude < -90 ||
      value.coordinates.latitude > 90 ||
      typeof value.coordinates.longitude !== "number" ||
      !Number.isFinite(value.coordinates.longitude) ||
      value.coordinates.longitude < -180 ||
      value.coordinates.longitude > 180
    ) {
      return null;
    }
    coordinates = {
      latitude: value.coordinates.latitude,
      longitude: value.coordinates.longitude,
    };
  }
  const images: Array<
    SpecialistLocationGatewayLocationPresentationV1["images"][number]
  > = [];
  const imageIds = new Set<string>();
  for (const image of value.images) {
    if (
      !isRecord(image) ||
      (!hasExactKeys(image, ["id", "src", "alt"]) &&
        !hasExactKeys(image, ["id", "src", "alt", "width", "height"])) ||
      !isSpecialistLocationGatewayIdentifier(image.id) ||
      imageIds.has(image.id) ||
      !isSafePublicMediaUrl(image.src) ||
      !isCanonicalCopy(image.alt) ||
      ("width" in image &&
        (!Number.isSafeInteger(image.width) || Number(image.width) <= 0)) ||
      ("height" in image &&
        (!Number.isSafeInteger(image.height) || Number(image.height) <= 0))
    ) {
      return null;
    }
    imageIds.add(image.id);
    images.push({
      id: image.id,
      src: image.src,
      alt: image.alt,
      ...(typeof image.width === "number" ? { width: image.width } : {}),
      ...(typeof image.height === "number" ? { height: image.height } : {}),
    });
  }
  if (value.imageSrc !== (images[0]?.src ?? null)) return null;
  return {
    name: value.name,
    address: value.address,
    city: value.city,
    state: value.state,
    countryCode: value.countryCode,
    timeZone: value.timeZone,
    coordinates,
    category: value.category,
    imageSrc: value.imageSrc as string | null,
    imageAlt: value.imageAlt,
    images,
    rating: value.rating as number | null,
    reviewCount: value.reviewCount as number,
    presentationRevision: value.presentationRevision,
  };
}

function cloneAppointmentAuthority(
  value: unknown,
): SpecialistLocationGatewayAppointmentAuthorityV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "directBookingEligible",
      "calendarBookingsEnabled",
      "onlineBookableOfferingCount",
      "organizerCatalog",
      "locationCatalog",
      "mappedOffering",
      "capabilities",
    ]) ||
    value.directBookingEligible !== true ||
    value.calendarBookingsEnabled !== true ||
    !Number.isSafeInteger(value.onlineBookableOfferingCount) ||
    Number(value.onlineBookableOfferingCount) < 1 ||
    !isRecord(value.capabilities) ||
    !hasExactKeys(value.capabilities, [
      "individual",
      "group",
      "groupAggregateActivationRevision",
    ]) ||
    typeof value.capabilities.individual !== "boolean" ||
    typeof value.capabilities.group !== "boolean" ||
    (!value.capabilities.individual && !value.capabilities.group) ||
    (value.capabilities.groupAggregateActivationRevision !== null &&
      !isSpecialistLocationGatewayIdentifier(
        value.capabilities.groupAggregateActivationRevision,
      )) ||
    (value.capabilities.group &&
      value.capabilities.groupAggregateActivationRevision === null)
  ) {
    return null;
  }
  const organizerCatalog = cloneCatalogReference(value.organizerCatalog);
  const locationCatalog = cloneCatalogReference(value.locationCatalog);
  const mappedOffering =
    value.mappedOffering === null
      ? null
      : cloneServiceMapping(value.mappedOffering);
  if (
    !organizerCatalog ||
    !locationCatalog ||
    (mappedOffering === null && value.mappedOffering !== null)
  ) {
    return null;
  }
  return {
    directBookingEligible: true,
    calendarBookingsEnabled: true,
    onlineBookableOfferingCount: value.onlineBookableOfferingCount as number,
    organizerCatalog,
    locationCatalog,
    mappedOffering,
    capabilities: {
      individual: value.capabilities.individual,
      group: value.capabilities.group,
      groupAggregateActivationRevision:
        value.capabilities.groupAggregateActivationRevision as string | null,
    },
  };
}

function clonePackageAuthority(
  value: unknown,
): SpecialistLocationGatewayPackageAuthorityV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "packageCatalogLocationId",
      "packageCatalogOwnerUserLocationId",
      "packageCatalogProviderId",
      "packageCatalogRevision",
      "packageCatalogVersion",
      "packageCatalogFingerprint",
      "mappedPackage",
      "assignmentAuthorizedPackageCount",
    ]) ||
    !Number.isSafeInteger(value.assignmentAuthorizedPackageCount) ||
    Number(value.assignmentAuthorizedPackageCount) < 1 ||
    !isSpecialistLocationGatewayIdentifier(value.packageCatalogLocationId) ||
    !isSpecialistLocationGatewayIdentifier(
      value.packageCatalogOwnerUserLocationId,
    ) ||
    !isSpecialistLocationGatewayIdentifier(value.packageCatalogProviderId) ||
    !isSpecialistLocationGatewayIdentifier(value.packageCatalogRevision) ||
    !isSpecialistLocationGatewayIdentifier(value.packageCatalogVersion) ||
    !isSpecialistLocationGatewayFingerprint(
      value.packageCatalogFingerprint,
    )
  ) {
    return null;
  }
  const mappedPackage =
    value.mappedPackage === null
      ? null
      : clonePackageMapping(value.mappedPackage);
  if (mappedPackage === null && value.mappedPackage !== null) return null;
  return {
    assignmentAuthorizedPackageCount:
      value.assignmentAuthorizedPackageCount as number,
    packageCatalogLocationId: value.packageCatalogLocationId,
    packageCatalogOwnerUserLocationId:
      value.packageCatalogOwnerUserLocationId,
    packageCatalogProviderId: value.packageCatalogProviderId,
    packageCatalogRevision: value.packageCatalogRevision,
    packageCatalogVersion: value.packageCatalogVersion,
    packageCatalogFingerprint: value.packageCatalogFingerprint,
    mappedPackage,
  };
}

function cloneEligibilityRow(
  value: unknown,
): SpecialistLocationGatewayEligibilityRowV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "order",
      "personId",
      "locationId",
      "userLocationId",
      "assignmentProfileRevision",
      "standaloneProfilePublished",
      "capabilityRevision",
      "presentation",
      "appointment",
      "packagePurchase",
    ]) ||
    !Number.isSafeInteger(value.order) ||
    Number(value.order) < 0 ||
    !isSpecialistLocationGatewayIdentifier(value.personId) ||
    !isSpecialistLocationGatewayIdentifier(value.locationId) ||
    !isSpecialistLocationGatewayIdentifier(value.userLocationId) ||
    !isSpecialistLocationGatewayIdentifier(value.assignmentProfileRevision) ||
    value.standaloneProfilePublished !== true ||
    !isSpecialistLocationGatewayIdentifier(value.capabilityRevision)
  ) {
    return null;
  }
  const presentation = cloneLocationPresentation(value.presentation);
  const appointment =
    value.appointment === null
      ? null
      : cloneAppointmentAuthority(value.appointment);
  const packagePurchase =
    value.packagePurchase === null
      ? null
      : clonePackageAuthority(value.packagePurchase);
  if (
    !presentation ||
    (appointment === null && value.appointment !== null) ||
    (packagePurchase === null && value.packagePurchase !== null) ||
    (!appointment && !packagePurchase)
  ) {
    return null;
  }
  return {
    order: value.order as number,
    personId: value.personId,
    locationId: value.locationId,
    userLocationId: value.userLocationId,
    assignmentProfileRevision: value.assignmentProfileRevision,
    standaloneProfilePublished: true,
    capabilityRevision: value.capabilityRevision,
    presentation,
    appointment,
    packagePurchase,
  };
}

function rowMatchesIntent(
  row: SpecialistLocationGatewayEligibilityRowV1,
  intent: SpecialistLocationGatewayIntentV1,
) {
  if (intent.kind === "package-purchase") {
    const mapping = row.packagePurchase?.mappedPackage;
    return (
      row.appointment === null &&
      mapping?.globalRef === intent.globalPackageRef &&
      mapping.sourcePackageId === intent.sourcePackageId
    );
  }
  if (intent.kind === "catalog" && intent.initialCatalog === "packages") {
    return (
      row.appointment === null &&
      row.packagePurchase !== null &&
      row.packagePurchase.mappedPackage === null
    );
  }
  if (intent.kind === "service" || intent.kind === "service-bundle") {
    const mapping = row.appointment?.mappedOffering;
    const expectedGlobalRef =
      intent.kind === "service"
        ? intent.globalServiceRef
        : intent.globalBundleRef;
    return (
      row.packagePurchase === null &&
      mapping?.kind === intent.kind &&
      mapping.globalRef === expectedGlobalRef &&
      mapping.sourceOfferingId === intent.sourceOfferingId
    );
  }
  return (
    row.packagePurchase === null &&
    row.appointment !== null &&
    row.appointment.mappedOffering === null
  );
}

function selectionMatchesIntent(
  selection: SpecialistLocationGatewayFrozenSelectionV1,
  intent: SpecialistLocationGatewayIntentV1,
  expectedProviderId?: string,
  expectedProviderRevision?: string,
) {
  if (
    (expectedProviderId !== undefined &&
      selection.gatewayProviderId !== expectedProviderId) ||
    (expectedProviderRevision !== undefined &&
      selection.gatewayProviderRevision !== expectedProviderRevision)
  ) {
    return false;
  }
  if (selection.authority.kind === "appointment") {
    const mapping = selection.authority.mappedOffering;
    if (
      selection.authority.organizerCatalog.locationId !==
        selection.selectedLocationId ||
      selection.authority.locationCatalog.locationId !==
        selection.selectedLocationId ||
      (mapping !== null &&
        (mapping.destinationLocationId !== selection.selectedLocationId ||
          mapping.destinationUserLocationId !==
            selection.selectedUserLocationId))
    ) {
      return false;
    }
  } else {
    const mapping = selection.authority.mappedPackage;
    if (
      selection.authority.packageCatalogLocationId !==
        selection.selectedLocationId ||
      selection.authority.packageCatalogOwnerUserLocationId !==
        selection.selectedUserLocationId ||
      (mapping !== null &&
        (mapping.destinationLocationId !== selection.selectedLocationId ||
          mapping.destinationUserLocationId !==
            selection.selectedUserLocationId))
    ) {
      return false;
    }
  }
  if (intent.kind === "package-purchase") {
    const mapping =
      selection.authority.kind === "package"
        ? selection.authority.mappedPackage
        : null;
    return (
      mapping?.globalRef === intent.globalPackageRef &&
      mapping.sourcePackageId === intent.sourcePackageId
    );
  }
  if (intent.kind === "catalog" && intent.initialCatalog === "packages") {
    return (
      selection.authority.kind === "package" &&
      selection.authority.mappedPackage === null
    );
  }
  if (selection.authority.kind !== "appointment") return false;
  if (intent.kind === "service" || intent.kind === "service-bundle") {
    const mapping = selection.authority.mappedOffering;
    return (
      mapping?.kind === intent.kind &&
      mapping.globalRef ===
        (intent.kind === "service"
          ? intent.globalServiceRef
          : intent.globalBundleRef) &&
      mapping.sourceOfferingId === intent.sourceOfferingId
    );
  }
  return selection.authority.mappedOffering === null;
}

export type SpecialistLocationGatewayPublicationValidation =
  | {
      valid: true;
      publication: SpecialistLocationGatewayPublicationV1;
    }
  | { valid: false; reason: string };

/** Strict provider boundary: no row can outlive or relabel its exact intent. */
export function validateSpecialistLocationGatewayPublication(
  value: unknown,
  input: {
    intentRecord: SpecialistLocationGatewayIntentRecordV1;
    nowEpochMs: number;
    expectedProviderId: string;
    expectedProviderRevision: string;
  },
): SpecialistLocationGatewayPublicationValidation {
  const intentValidation = validateSpecialistLocationGatewayIntentRecord(
    input.intentRecord,
  );
  if (
    !intentValidation.valid ||
    !isSpecialistLocationGatewayRecordFresh(
      intentValidation.draft,
      input.nowEpochMs,
    ) ||
    !isRecord(value) ||
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "providerId",
      "providerRevision",
      "intentDraftId",
      "intentOwnerKey",
      "sourceProfileRevision",
      "sourceCatalogVersion",
      "personId",
      "sourceLocationId",
      "sourceUserLocationId",
      "locale",
      "generation",
      "publishedAtEpochMs",
      "expiresAtEpochMs",
      "rows",
    ]) ||
    value.schemaVersion !== SPECIALIST_LOCATION_GATEWAY_PROVIDER_SCHEMA_VERSION ||
    value.kind !== "specialist-location-gateway-publication" ||
    !isSpecialistLocationGatewayIdentifier(value.providerId) ||
    !isSpecialistLocationGatewayIdentifier(value.providerRevision) ||
    value.providerId !== intentValidation.draft.gatewayProviderId ||
    value.providerRevision !==
      intentValidation.draft.gatewayProviderRevision ||
    value.intentDraftId !== intentValidation.draft.draftId ||
    value.intentOwnerKey !== intentValidation.draft.ownerKey ||
    value.sourceProfileRevision !==
      intentValidation.draft.source.sourceProfileRevision ||
    value.sourceCatalogVersion !==
      intentValidation.draft.source.sourceCatalogVersion ||
    value.providerId !== input.expectedProviderId ||
    value.providerRevision !== input.expectedProviderRevision ||
    value.personId !== intentValidation.draft.source.personId ||
    value.sourceLocationId !==
      intentValidation.draft.source.sourceLocationId ||
    value.sourceUserLocationId !==
      intentValidation.draft.source.sourceUserLocationId ||
    value.locale !== intentValidation.draft.locale ||
    value.generation !== intentValidation.draft.generation ||
    !isEpoch(value.publishedAtEpochMs) ||
    !isEpoch(value.expiresAtEpochMs) ||
    value.publishedAtEpochMs >
      input.nowEpochMs + SPECIALIST_LOCATION_GATEWAY_MAX_CLOCK_SKEW_MS ||
    value.publishedAtEpochMs < intentValidation.draft.createdAtEpochMs ||
    value.expiresAtEpochMs <= input.nowEpochMs ||
    value.expiresAtEpochMs <= value.publishedAtEpochMs ||
    value.expiresAtEpochMs - value.publishedAtEpochMs >
      SPECIALIST_LOCATION_GATEWAY_MAX_TTL_MS ||
    !isSpecialistLocationGatewayPublicationRowCount(value.rows)
  ) {
    return { valid: false, reason: "invalid-specialist-location-gateway-publication" };
  }

  const rows: SpecialistLocationGatewayEligibilityRowV1[] = [];
  const userLocationIds = new Set<string>();
  const locationIds = new Set<string>();
  for (let index = 0; index < value.rows.length; index += 1) {
    const row = cloneEligibilityRow(value.rows[index]);
    if (
      !row ||
      row.order !== index ||
      row.personId !== intentValidation.draft.source.personId ||
      userLocationIds.has(row.userLocationId) ||
      locationIds.has(row.locationId) ||
      !rowMatchesIntent(row, intentValidation.draft.intent) ||
      (row.appointment !== null &&
        (row.appointment.organizerCatalog.locationId !== row.locationId ||
          row.appointment.locationCatalog.locationId !== row.locationId ||
          row.appointment.organizerCatalog.catalogOwnerUserLocationId !==
          row.userLocationId ||
          (row.appointment.mappedOffering !== null &&
            (row.appointment.mappedOffering.destinationLocationId !==
              row.locationId ||
              row.appointment.mappedOffering.destinationUserLocationId !==
                row.userLocationId ||
              row.appointment.mappedOffering.destinationCatalogVersion !==
                row.appointment.organizerCatalog.catalogVersion)))) ||
      (row.packagePurchase !== null &&
        (row.packagePurchase.packageCatalogLocationId !== row.locationId ||
          row.packagePurchase.packageCatalogOwnerUserLocationId !==
            row.userLocationId)) ||
      (row.packagePurchase?.mappedPackage !== null &&
        row.packagePurchase?.mappedPackage !== undefined &&
        (row.packagePurchase.mappedPackage.destinationLocationId !==
          row.locationId ||
          row.packagePurchase.mappedPackage.destinationUserLocationId !==
            row.userLocationId ||
          row.packagePurchase.mappedPackage.destinationCatalogVersion !==
            row.packagePurchase.packageCatalogVersion ||
          row.packagePurchase.mappedPackage.publicationProviderId !==
            row.packagePurchase.packageCatalogProviderId))
    ) {
      return { valid: false, reason: "invalid-specialist-location-gateway-row" };
    }
    userLocationIds.add(row.userLocationId);
    locationIds.add(row.locationId);
    rows.push(row);
  }

  return {
    valid: true,
    publication: {
      schemaVersion: SPECIALIST_LOCATION_GATEWAY_PROVIDER_SCHEMA_VERSION,
      kind: "specialist-location-gateway-publication",
      providerId: value.providerId,
      providerRevision: value.providerRevision,
      intentDraftId: value.intentDraftId as string,
      intentOwnerKey: value.intentOwnerKey as string,
      sourceProfileRevision: value.sourceProfileRevision as string,
      sourceCatalogVersion: value.sourceCatalogVersion as string,
      personId: value.personId as string,
      sourceLocationId: value.sourceLocationId as string,
      sourceUserLocationId: value.sourceUserLocationId as string,
      locale: value.locale as string,
      generation: value.generation as number,
      publishedAtEpochMs: value.publishedAtEpochMs,
      expiresAtEpochMs: value.expiresAtEpochMs,
      rows,
    },
  };
}

export function createSpecialistLocationGatewayNavigationOwnerKey(input: {
  phase:
    | "auth-rebind"
    | "catalog-transition"
    | "claim"
    | "committed-auth-rebind"
    | "committed"
    | "destination-lifecycle"
    | "pre-draft";
  draftId: string;
  gatewayProviderId: string;
  gatewayProviderRevision: string;
  customerAccountId: string | null;
  locale: string;
  generation: number;
  intentDraftId: string;
  createdAtEpochMs: number;
  expiresAtEpochMs: number;
  sourceAction: SpecialistLocationGatewaySourceAction;
  source: SpecialistLocationGatewaySourceV1;
  intent: SpecialistLocationGatewayIntentV1;
  selection: SpecialistLocationGatewayFrozenSelectionV1;
  attemptId?: string;
  resolutionId?: string;
  destinationKind?: SpecialistLocationGatewayDestinationKind;
  destinationDraftId?: string;
  destinationDraftFingerprint?: string;
  claimedAtEpochMs?: number;
  committedAtEpochMs?: number;
  targetCatalog?: "packages" | "services";
  targetGeneration?: number;
  authenticatedCustomerAccountId?: string;
  targetCreatedAtEpochMs?: number;
  targetExpiresAtEpochMs?: number;
  lineageAttemptId?: string;
  lifecycleAttemptId?: string;
  lifecycleAction?: "close" | "return-to-pre-draft";
  mutableCleanupFingerprint?: string;
  lineageCommittedAtEpochMs?: number;
  authAttemptId?: string;
  priorDestinationDraftFingerprint?: string;
  authenticatedDestinationDraftFingerprint?: string;
  destinationOwnerBindingMode?: "companion-only" | "durable-owner";
}) {
  const phaseFields: (string | number | boolean | null)[] =
    input.phase === "committed"
      ? [
          input.resolutionId ?? null,
          input.attemptId ?? null,
          input.destinationKind ?? null,
          input.destinationDraftId ?? null,
          input.destinationDraftFingerprint ?? null,
          input.committedAtEpochMs ?? null,
        ]
      : input.phase === "claim"
        ? [
            input.attemptId ?? null,
            input.destinationKind ?? null,
            input.destinationDraftId ?? null,
            input.destinationDraftFingerprint ?? null,
            input.claimedAtEpochMs ?? null,
          ]
        : input.phase === "catalog-transition"
          ? [
              input.attemptId ?? null,
              input.targetCatalog ?? null,
              input.targetGeneration ?? null,
              input.claimedAtEpochMs ?? null,
            ]
          : input.phase === "auth-rebind"
            ? [
                input.attemptId ?? null,
                input.authenticatedCustomerAccountId ?? null,
                input.targetGeneration ?? null,
                input.targetCreatedAtEpochMs ?? null,
                input.targetExpiresAtEpochMs ?? null,
                input.claimedAtEpochMs ?? null,
              ]
            : input.phase === "destination-lifecycle"
              ? [
                  input.resolutionId ?? null,
                  input.lineageAttemptId ?? null,
                  input.lifecycleAttemptId ?? null,
                  input.lifecycleAction ?? null,
                  input.destinationKind ?? null,
                  input.destinationDraftId ?? null,
                  input.destinationDraftFingerprint ?? null,
                  input.mutableCleanupFingerprint ?? null,
                  input.lineageCommittedAtEpochMs ?? null,
                  input.claimedAtEpochMs ?? null,
                ]
              : input.phase === "committed-auth-rebind"
                ? [
                    input.resolutionId ?? null,
                    input.lineageAttemptId ?? null,
                    input.authAttemptId ?? null,
                    input.destinationKind ?? null,
                    input.destinationDraftId ?? null,
                    input.priorDestinationDraftFingerprint ?? null,
                    input.authenticatedDestinationDraftFingerprint ?? null,
                    input.destinationOwnerBindingMode ?? null,
                    input.authenticatedCustomerAccountId ?? null,
                    input.targetGeneration ?? null,
                    input.targetCreatedAtEpochMs ?? null,
                    input.targetExpiresAtEpochMs ?? null,
                    input.lineageCommittedAtEpochMs ?? null,
                    input.claimedAtEpochMs ?? null,
                  ]
              : [];
  return createSpecialistLocationGatewayOwnerKey({
    stage: `navigation-authority-v1:${input.phase}`,
    fields: [
      input.draftId,
      input.gatewayProviderId,
      input.gatewayProviderRevision,
      input.customerAccountId,
      input.locale,
      input.generation,
      input.intentDraftId,
      input.createdAtEpochMs,
      input.expiresAtEpochMs,
      input.sourceAction,
      ...sourceOwnerFields(input.source),
      ...intentOwnerFields(input.intent),
      ...selectionOwnerFields(input.selection),
      ...phaseFields,
    ],
  });
}

export function createSpecialistLocationGatewayPreDraftResolution(input: {
  resolutionId: string;
  intentRecord: SpecialistLocationGatewayIntentRecordV1;
  publication: SpecialistLocationGatewayPublicationV1;
  selectedRow: SpecialistLocationGatewayEligibilityRowV1;
  nowEpochMs: number;
}): SpecialistLocationGatewayPreDraftResolutionV1 | null {
  if (
    !isSpecialistLocationGatewayIdentifier(input.resolutionId) ||
    input.resolutionId !== input.intentRecord.draftId
  ) {
    return null;
  }
  const publicationValidation = validateSpecialistLocationGatewayPublication(
    input.publication,
    {
      intentRecord: input.intentRecord,
      nowEpochMs: input.nowEpochMs,
      expectedProviderId: input.intentRecord.gatewayProviderId,
      expectedProviderRevision: input.intentRecord.gatewayProviderRevision,
    },
  );
  if (!publicationValidation.valid) return null;
  const selectedRows = publicationValidation.publication.rows.filter(
    (row) =>
      row.locationId === input.selectedRow.locationId &&
      row.userLocationId === input.selectedRow.userLocationId,
  );
  if (
    selectedRows.length !== 1 ||
    JSON.stringify(selectedRows[0]) !== JSON.stringify(input.selectedRow)
  ) {
    return null;
  }
  const intentRecord = validateSpecialistLocationGatewayIntentRecord(
    input.intentRecord,
  );
  if (!intentRecord.valid) return null;
  const selectedRow = selectedRows[0]!;
  const publication = publicationValidation.publication;
  const appointment = selectedRow.appointment;
  const packagePurchase = selectedRow.packagePurchase;
  const needsPackage =
    intentRecord.draft.intent.kind === "package-purchase" ||
    (intentRecord.draft.intent.kind === "catalog" &&
      intentRecord.draft.intent.initialCatalog === "packages");
  const authority: SpecialistLocationGatewaySelectedAuthorityReferenceV1 | null =
    needsPackage
      ? packagePurchase
        ? {
            kind: "package",
            packageCatalogLocationId:
              packagePurchase.packageCatalogLocationId,
            packageCatalogOwnerUserLocationId:
              packagePurchase.packageCatalogOwnerUserLocationId,
            packageCatalogProviderId:
              packagePurchase.packageCatalogProviderId,
            packageCatalogRevision: packagePurchase.packageCatalogRevision,
            packageCatalogVersion: packagePurchase.packageCatalogVersion,
            packageCatalogFingerprint:
              packagePurchase.packageCatalogFingerprint,
            mappedPackage: packagePurchase.mappedPackage,
          }
        : null
      : appointment
        ? {
            kind: "appointment",
            organizerCatalog: appointment.organizerCatalog,
            locationCatalog: appointment.locationCatalog,
            mappedOffering: appointment.mappedOffering,
            individualCapability: appointment.capabilities.individual,
            groupCapability: appointment.capabilities.group,
            groupAggregateActivationRevision:
              appointment.capabilities.groupAggregateActivationRevision,
          }
        : packagePurchase &&
            intentRecord.draft.intent.kind === "catalog" &&
            intentRecord.draft.intent.initialCatalog === "packages"
          ? {
              kind: "package",
              packageCatalogLocationId:
                packagePurchase.packageCatalogLocationId,
              packageCatalogOwnerUserLocationId:
                packagePurchase.packageCatalogOwnerUserLocationId,
              packageCatalogProviderId:
                packagePurchase.packageCatalogProviderId,
              packageCatalogRevision: packagePurchase.packageCatalogRevision,
              packageCatalogVersion: packagePurchase.packageCatalogVersion,
              packageCatalogFingerprint:
                packagePurchase.packageCatalogFingerprint,
              mappedPackage: packagePurchase.mappedPackage,
            }
          : null;
  if (!authority) return null;

  const body = {
    draftId: input.resolutionId,
    gatewayProviderId: intentRecord.draft.gatewayProviderId,
    gatewayProviderRevision: intentRecord.draft.gatewayProviderRevision,
    customerAccountId: intentRecord.draft.customerAccountId,
    locale: intentRecord.draft.locale,
    generation: intentRecord.draft.generation,
    intentDraftId: intentRecord.draft.draftId,
    createdAtEpochMs: publication.publishedAtEpochMs,
    expiresAtEpochMs: Math.min(
      intentRecord.draft.expiresAtEpochMs,
      publication.expiresAtEpochMs,
    ),
    sourceAction: intentRecord.draft.sourceAction,
    source: intentRecord.draft.source,
    intent: intentRecord.draft.intent,
    selection: {
      gatewayProviderId: publication.providerId,
      gatewayProviderRevision: publication.providerRevision,
      gatewayCapabilityRevision: selectedRow.capabilityRevision,
      selectedLocationId: selectedRow.locationId,
      selectedUserLocationId: selectedRow.userLocationId,
      selectedAssignmentProfileRevision:
        selectedRow.assignmentProfileRevision,
      authority,
    } satisfies SpecialistLocationGatewayFrozenSelectionV1,
  };

  return {
    schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
    kind: "specialist-location-gateway-pre-draft-resolution",
    mutationRevision: 0,
    ...body,
    ownerKey: createSpecialistLocationGatewayNavigationOwnerKey({
      phase: "pre-draft",
      ...body,
    }),
  };
}

function preDraftResolutionMatchesIntentRecordExact(
  resolution: SpecialistLocationGatewayPreDraftResolutionV1,
  intentRecord: SpecialistLocationGatewayIntentRecordV1,
) {
  try {
    return (
      resolution.draftId === intentRecord.draftId &&
      resolution.intentDraftId === intentRecord.draftId &&
      resolution.gatewayProviderId === intentRecord.gatewayProviderId &&
      resolution.gatewayProviderRevision ===
        intentRecord.gatewayProviderRevision &&
      resolution.customerAccountId === intentRecord.customerAccountId &&
      resolution.locale === intentRecord.locale &&
      resolution.generation === intentRecord.generation &&
      resolution.createdAtEpochMs >= intentRecord.createdAtEpochMs &&
      resolution.expiresAtEpochMs <= intentRecord.expiresAtEpochMs &&
      resolution.sourceAction === intentRecord.sourceAction &&
      JSON.stringify(resolution.source) ===
        JSON.stringify(intentRecord.source) &&
      JSON.stringify(resolution.intent) === JSON.stringify(intentRecord.intent)
    );
  } catch {
    return false;
  }
}

function createSpecialistLocationGatewayPreDraftGcOwnerKey(input: {
  intentRecord: SpecialistLocationGatewayIntentRecordV1;
  priorResolution: SpecialistLocationGatewayPreDraftResolutionV1 | null;
  claimedAtEpochMs: number;
}) {
  return createSpecialistLocationGatewayOwnerKey({
    stage: "navigation-authority-v1:pre-draft-gc",
    fields: [
      input.intentRecord.ownerKey,
      input.priorResolution?.ownerKey ?? null,
      input.claimedAtEpochMs,
    ],
  });
}

export function createSpecialistLocationGatewayPreDraftGcClaim(input: {
  intentRecord: SpecialistLocationGatewayIntentRecordV1;
  priorResolution: SpecialistLocationGatewayPreDraftResolutionV1 | null;
  claimedAtEpochMs: number;
}): SpecialistLocationGatewayPreDraftGcClaimV1 | null {
  const intentValidation = validateSpecialistLocationGatewayIntentRecord(
    input.intentRecord,
  );
  if (
    !intentValidation.valid ||
    !isEpoch(input.claimedAtEpochMs) ||
    input.claimedAtEpochMs < intentValidation.draft.expiresAtEpochMs
  ) {
    return null;
  }

  let priorResolution: SpecialistLocationGatewayPreDraftResolutionV1 | null =
    null;
  if (input.priorResolution !== null) {
    const priorValidation =
      validateSpecialistLocationGatewayNavigationAuthority(
        input.priorResolution,
      );
    if (
      !priorValidation.valid ||
      priorValidation.draft.kind !==
        "specialist-location-gateway-pre-draft-resolution" ||
      !preDraftResolutionMatchesIntentRecordExact(
        priorValidation.draft,
        intentValidation.draft,
      ) ||
      input.claimedAtEpochMs < priorValidation.draft.expiresAtEpochMs
    ) {
      return null;
    }
    priorResolution = priorValidation.draft;
  }

  const intentRecord = intentValidation.draft;
  const claimBody = {
    draftId: intentRecord.draftId,
    intentOwnerKey: intentRecord.ownerKey,
    gatewayProviderId: intentRecord.gatewayProviderId,
    gatewayProviderRevision: intentRecord.gatewayProviderRevision,
    customerAccountId: intentRecord.customerAccountId,
    locale: intentRecord.locale,
    generation: intentRecord.generation,
    intentDraftId: intentRecord.draftId,
    createdAtEpochMs: intentRecord.createdAtEpochMs,
    expiresAtEpochMs: intentRecord.expiresAtEpochMs,
    sourceAction: intentRecord.sourceAction,
    source: intentRecord.source,
    intent: intentRecord.intent,
    priorResolution,
    claimedAtEpochMs: input.claimedAtEpochMs,
  };
  return {
    schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
    kind: "specialist-location-gateway-pre-draft-gc-claim",
    mutationRevision: 7,
    ...claimBody,
    ownerKey: createSpecialistLocationGatewayPreDraftGcOwnerKey({
      intentRecord,
      priorResolution,
      claimedAtEpochMs: input.claimedAtEpochMs,
    }),
  };
}

function validateSpecialistLocationGatewayPreDraftGcClaim(
  value: Record<string, unknown>,
): SpecialistLocationGatewayValidation<SpecialistLocationGatewayPreDraftGcClaimV1> {
  if (
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "mutationRevision",
      "draftId",
      "ownerKey",
      "intentOwnerKey",
      "gatewayProviderId",
      "gatewayProviderRevision",
      "customerAccountId",
      "locale",
      "generation",
      "intentDraftId",
      "createdAtEpochMs",
      "expiresAtEpochMs",
      "sourceAction",
      "source",
      "intent",
      "priorResolution",
      "claimedAtEpochMs",
    ]) ||
    value.schemaVersion !== SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION ||
    value.kind !== "specialist-location-gateway-pre-draft-gc-claim" ||
    value.mutationRevision !== 7 ||
    value.intentDraftId !== value.draftId ||
    typeof value.intentOwnerKey !== "string" ||
    typeof value.ownerKey !== "string" ||
    value.ownerKey.length === 0 ||
    value.ownerKey.length > MAX_OWNER_KEY_LENGTH ||
    !isEpoch(value.claimedAtEpochMs)
  ) {
    return {
      valid: false,
      reason: "invalid-specialist-location-gateway-pre-draft-gc-claim",
    };
  }

  const intentValidation = validateSpecialistLocationGatewayIntentRecord({
    schemaVersion: value.schemaVersion,
    kind: "specialist-location-gateway-intent",
    draftId: value.draftId,
    ownerKey: value.intentOwnerKey,
    gatewayProviderId: value.gatewayProviderId,
    gatewayProviderRevision: value.gatewayProviderRevision,
    customerAccountId: value.customerAccountId,
    locale: value.locale,
    generation: value.generation,
    createdAtEpochMs: value.createdAtEpochMs,
    expiresAtEpochMs: value.expiresAtEpochMs,
    sourceAction: value.sourceAction,
    source: value.source,
    intent: value.intent,
  });
  if (
    !intentValidation.valid ||
    value.claimedAtEpochMs < intentValidation.draft.expiresAtEpochMs
  ) {
    return {
      valid: false,
      reason: "invalid-specialist-location-gateway-pre-draft-gc-claim",
    };
  }

  let priorResolution: SpecialistLocationGatewayPreDraftResolutionV1 | null =
    null;
  if (value.priorResolution !== null) {
    if (
      !isRecord(value.priorResolution) ||
      value.priorResolution.kind !==
        "specialist-location-gateway-pre-draft-resolution"
    ) {
      return {
        valid: false,
        reason: "invalid-specialist-location-gateway-pre-draft-gc-claim",
      };
    }
    const priorValidation =
      validateSpecialistLocationGatewayNavigationAuthority(
        value.priorResolution,
      );
    if (
      !priorValidation.valid ||
      priorValidation.draft.kind !==
        "specialist-location-gateway-pre-draft-resolution" ||
      !preDraftResolutionMatchesIntentRecordExact(
        priorValidation.draft,
        intentValidation.draft,
      ) ||
      value.claimedAtEpochMs < priorValidation.draft.expiresAtEpochMs
    ) {
      return {
        valid: false,
        reason: "invalid-specialist-location-gateway-pre-draft-gc-claim",
      };
    }
    priorResolution = priorValidation.draft;
  }

  const expectedOwnerKey =
    createSpecialistLocationGatewayPreDraftGcOwnerKey({
      intentRecord: intentValidation.draft,
      priorResolution,
      claimedAtEpochMs: value.claimedAtEpochMs,
    });
  if (value.ownerKey !== expectedOwnerKey) {
    return {
      valid: false,
      reason: "specialist-location-gateway-authority-owner-mismatch",
    };
  }

  const intentRecord = intentValidation.draft;
  return {
    valid: true,
    draft: {
      schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
      kind: "specialist-location-gateway-pre-draft-gc-claim",
      mutationRevision: 7,
      draftId: intentRecord.draftId,
      ownerKey: expectedOwnerKey,
      intentOwnerKey: intentRecord.ownerKey,
      gatewayProviderId: intentRecord.gatewayProviderId,
      gatewayProviderRevision: intentRecord.gatewayProviderRevision,
      customerAccountId: intentRecord.customerAccountId,
      locale: intentRecord.locale,
      generation: intentRecord.generation,
      intentDraftId: intentRecord.draftId,
      createdAtEpochMs: intentRecord.createdAtEpochMs,
      expiresAtEpochMs: intentRecord.expiresAtEpochMs,
      sourceAction: intentRecord.sourceAction,
      source: intentRecord.source,
      intent: intentRecord.intent,
      priorResolution,
      claimedAtEpochMs: value.claimedAtEpochMs,
    },
  };
}

export function validateSpecialistLocationGatewayNavigationAuthority(
  value: unknown,
): SpecialistLocationGatewayValidation<SpecialistLocationGatewayNavigationAuthorityV1> {
  if (
    isRecord(value) &&
    value.kind === "specialist-location-gateway-pre-draft-gc-claim"
  ) {
    return validateSpecialistLocationGatewayPreDraftGcClaim(value);
  }
  const baseKeys = [
    "schemaVersion",
    "kind",
    "mutationRevision",
    "draftId",
    "ownerKey",
    "gatewayProviderId",
    "gatewayProviderRevision",
    "customerAccountId",
    "locale",
    "generation",
    "intentDraftId",
    "createdAtEpochMs",
    "expiresAtEpochMs",
    "sourceAction",
    "source",
    "intent",
    "selection",
  ] as const;
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      value.kind === "specialist-location-gateway-navigation-lineage"
        ? [
            ...baseKeys,
            "resolutionId",
            "attemptId",
            "destinationKind",
            "destinationDraftId",
            "destinationDraftFingerprint",
            "committedAtEpochMs",
          ]
        : value.kind === "specialist-location-gateway-navigation-commit-claim"
          ? [
              ...baseKeys,
              "attemptId",
              "destinationKind",
              "destinationDraftId",
              "destinationDraftFingerprint",
              "claimedAtEpochMs",
            ]
          : value.kind ===
              "specialist-location-gateway-catalog-transition-claim"
            ? [
                ...baseKeys,
                "attemptId",
                "targetCatalog",
                "targetGeneration",
                "claimedAtEpochMs",
              ]
            : value.kind ===
                "specialist-location-gateway-auth-rebind-claim"
              ? [
                  ...baseKeys,
                  "attemptId",
                  "authenticatedCustomerAccountId",
                  "targetGeneration",
                  "targetCreatedAtEpochMs",
                  "targetExpiresAtEpochMs",
                  "claimedAtEpochMs",
                ]
              : value.kind ===
                  "specialist-location-gateway-destination-lifecycle-claim"
                ? [
                    ...baseKeys,
                    "resolutionId",
                    "lineageAttemptId",
                    "lifecycleAttemptId",
                    "action",
                    "destinationKind",
                    "destinationDraftId",
                    "destinationDraftFingerprint",
                    "mutableCleanupFingerprint",
                    "lineageCommittedAtEpochMs",
                    "claimedAtEpochMs",
                  ]
                : value.kind ===
                    "specialist-location-gateway-committed-auth-rebind-claim"
                  ? [
                      ...baseKeys,
                      "resolutionId",
                      "lineageAttemptId",
                      "authAttemptId",
                      "destinationKind",
                      "destinationDraftId",
                      "priorDestinationDraftFingerprint",
                      "authenticatedDestinationDraftFingerprint",
                      "destinationOwnerBindingMode",
                      "authenticatedCustomerAccountId",
                      "targetGeneration",
                      "targetCreatedAtEpochMs",
                      "targetExpiresAtEpochMs",
                      "lineageCommittedAtEpochMs",
                      "claimedAtEpochMs",
                    ]
              : baseKeys,
    ) ||
    value.schemaVersion !== SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION ||
    (value.kind !== "specialist-location-gateway-pre-draft-resolution" &&
      value.kind !== "specialist-location-gateway-navigation-commit-claim" &&
      value.kind !==
        "specialist-location-gateway-catalog-transition-claim" &&
      value.kind !== "specialist-location-gateway-auth-rebind-claim" &&
      value.kind !==
        "specialist-location-gateway-destination-lifecycle-claim" &&
      value.kind !==
        "specialist-location-gateway-committed-auth-rebind-claim" &&
      value.kind !== "specialist-location-gateway-navigation-lineage") ||
    !isSpecialistLocationGatewayIdentifier(value.draftId) ||
    typeof value.ownerKey !== "string" ||
    value.ownerKey.length === 0 ||
    value.ownerKey.length > MAX_OWNER_KEY_LENGTH ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayProviderId) ||
    !isSpecialistLocationGatewayIdentifier(value.gatewayProviderRevision) ||
    (value.customerAccountId !== null &&
      !isSpecialistLocationGatewayIdentifier(value.customerAccountId)) ||
    !isLocale(value.locale) ||
    !Number.isSafeInteger(value.generation) ||
    Number(value.generation) < 0 ||
    !isSpecialistLocationGatewayIdentifier(value.intentDraftId) ||
    value.draftId !== value.intentDraftId ||
    !hasValidLifetime(value) ||
    !isSourceAction(value.sourceAction)
  ) {
    return { valid: false, reason: "invalid-specialist-location-gateway-authority" };
  }
  const source = cloneSource(value.source);
  const intent = cloneIntent(value.intent);
  const selection = cloneFrozenSelection(value.selection);
  if (
    !source ||
    !intent ||
    !selection ||
    !sourceActionMatchesIntent(value.sourceAction, intent) ||
    !selectionMatchesIntent(
      selection,
      intent,
      value.gatewayProviderId,
      value.gatewayProviderRevision,
    )
  ) {
    return { valid: false, reason: "invalid-specialist-location-gateway-authority" };
  }

  const body = {
    draftId: value.draftId,
    gatewayProviderId: value.gatewayProviderId,
    gatewayProviderRevision: value.gatewayProviderRevision,
    customerAccountId: value.customerAccountId as string | null,
    locale: value.locale,
    generation: value.generation as number,
    intentDraftId: value.intentDraftId,
    createdAtEpochMs: value.createdAtEpochMs as number,
    expiresAtEpochMs: value.expiresAtEpochMs as number,
    sourceAction: value.sourceAction,
    source,
    intent,
    selection,
  };
  const expectedOwnerKey =
    value.kind === "specialist-location-gateway-navigation-lineage"
      ? createSpecialistLocationGatewayNavigationOwnerKey({
          phase: "committed",
          ...body,
          resolutionId:
            typeof value.resolutionId === "string" ? value.resolutionId : "",
          attemptId:
            typeof value.attemptId === "string" ? value.attemptId : "",
          destinationKind:
            value.destinationKind as SpecialistLocationGatewayDestinationKind,
          destinationDraftId:
            typeof value.destinationDraftId === "string"
              ? value.destinationDraftId
              : "",
          destinationDraftFingerprint:
            typeof value.destinationDraftFingerprint === "string"
              ? value.destinationDraftFingerprint
              : "",
          committedAtEpochMs:
            typeof value.committedAtEpochMs === "number"
              ? value.committedAtEpochMs
              : -1,
        })
      : value.kind === "specialist-location-gateway-navigation-commit-claim"
        ? createSpecialistLocationGatewayNavigationOwnerKey({
            phase: "claim",
            ...body,
            attemptId:
              typeof value.attemptId === "string" ? value.attemptId : "",
            destinationKind:
              value.destinationKind as SpecialistLocationGatewayDestinationKind,
            destinationDraftId:
              typeof value.destinationDraftId === "string"
                ? value.destinationDraftId
                : "",
            destinationDraftFingerprint:
              typeof value.destinationDraftFingerprint === "string"
                ? value.destinationDraftFingerprint
                : "",
            claimedAtEpochMs:
              typeof value.claimedAtEpochMs === "number"
                ? value.claimedAtEpochMs
                : -1,
          })
        : value.kind ===
            "specialist-location-gateway-catalog-transition-claim"
          ? createSpecialistLocationGatewayNavigationOwnerKey({
              phase: "catalog-transition",
              ...body,
              attemptId:
                typeof value.attemptId === "string" ? value.attemptId : "",
              targetCatalog:
                value.targetCatalog === "packages" ||
                value.targetCatalog === "services"
                  ? value.targetCatalog
                  : undefined,
              targetGeneration:
                typeof value.targetGeneration === "number"
                  ? value.targetGeneration
                  : -1,
              claimedAtEpochMs:
                typeof value.claimedAtEpochMs === "number"
                  ? value.claimedAtEpochMs
                  : -1,
            })
          : value.kind ===
              "specialist-location-gateway-auth-rebind-claim"
            ? createSpecialistLocationGatewayNavigationOwnerKey({
                phase: "auth-rebind",
                ...body,
                attemptId:
                  typeof value.attemptId === "string" ? value.attemptId : "",
                authenticatedCustomerAccountId:
                  typeof value.authenticatedCustomerAccountId === "string"
                    ? value.authenticatedCustomerAccountId
                    : "",
                targetGeneration:
                  typeof value.targetGeneration === "number"
                    ? value.targetGeneration
                    : -1,
                targetCreatedAtEpochMs:
                  typeof value.targetCreatedAtEpochMs === "number"
                    ? value.targetCreatedAtEpochMs
                    : -1,
                targetExpiresAtEpochMs:
                  typeof value.targetExpiresAtEpochMs === "number"
                    ? value.targetExpiresAtEpochMs
                    : -1,
                claimedAtEpochMs:
                  typeof value.claimedAtEpochMs === "number"
                    ? value.claimedAtEpochMs
                    : -1,
              })
            : value.kind ===
                "specialist-location-gateway-destination-lifecycle-claim"
              ? createSpecialistLocationGatewayNavigationOwnerKey({
                  phase: "destination-lifecycle",
                  ...body,
                  resolutionId:
                    typeof value.resolutionId === "string"
                      ? value.resolutionId
                      : "",
                  lineageAttemptId:
                    typeof value.lineageAttemptId === "string"
                      ? value.lineageAttemptId
                      : "",
                  lifecycleAttemptId:
                    typeof value.lifecycleAttemptId === "string"
                      ? value.lifecycleAttemptId
                      : "",
                  lifecycleAction:
                    value.action === "close" ||
                    value.action === "return-to-pre-draft"
                      ? value.action
                      : undefined,
                  destinationKind:
                    value.destinationKind as SpecialistLocationGatewayDestinationKind,
                  destinationDraftId:
                    typeof value.destinationDraftId === "string"
                      ? value.destinationDraftId
                      : "",
                  destinationDraftFingerprint:
                    typeof value.destinationDraftFingerprint === "string"
                      ? value.destinationDraftFingerprint
                      : "",
                  mutableCleanupFingerprint:
                    typeof value.mutableCleanupFingerprint === "string"
                      ? value.mutableCleanupFingerprint
                      : "",
                  lineageCommittedAtEpochMs:
                    typeof value.lineageCommittedAtEpochMs === "number"
                      ? value.lineageCommittedAtEpochMs
                      : -1,
                  claimedAtEpochMs:
                    typeof value.claimedAtEpochMs === "number"
                      ? value.claimedAtEpochMs
                      : -1,
                })
              : value.kind ===
                  "specialist-location-gateway-committed-auth-rebind-claim"
                ? createSpecialistLocationGatewayNavigationOwnerKey({
                    phase: "committed-auth-rebind",
                    ...body,
                    resolutionId:
                      typeof value.resolutionId === "string"
                        ? value.resolutionId
                        : "",
                    lineageAttemptId:
                      typeof value.lineageAttemptId === "string"
                        ? value.lineageAttemptId
                        : "",
                    authAttemptId:
                      typeof value.authAttemptId === "string"
                        ? value.authAttemptId
                        : "",
                    destinationKind:
                      value.destinationKind as SpecialistLocationGatewayDestinationKind,
                    destinationDraftId:
                      typeof value.destinationDraftId === "string"
                        ? value.destinationDraftId
                        : "",
                    priorDestinationDraftFingerprint:
                      typeof value.priorDestinationDraftFingerprint ===
                      "string"
                        ? value.priorDestinationDraftFingerprint
                        : "",
                    authenticatedDestinationDraftFingerprint:
                      typeof value.authenticatedDestinationDraftFingerprint ===
                      "string"
                        ? value.authenticatedDestinationDraftFingerprint
                        : "",
                    destinationOwnerBindingMode:
                      value.destinationOwnerBindingMode === "companion-only" ||
                      value.destinationOwnerBindingMode === "durable-owner"
                        ? value.destinationOwnerBindingMode
                        : undefined,
                    authenticatedCustomerAccountId:
                      typeof value.authenticatedCustomerAccountId === "string"
                        ? value.authenticatedCustomerAccountId
                        : "",
                    targetGeneration:
                      typeof value.targetGeneration === "number"
                        ? value.targetGeneration
                        : -1,
                    targetCreatedAtEpochMs:
                      typeof value.targetCreatedAtEpochMs === "number"
                        ? value.targetCreatedAtEpochMs
                        : -1,
                    targetExpiresAtEpochMs:
                      typeof value.targetExpiresAtEpochMs === "number"
                        ? value.targetExpiresAtEpochMs
                        : -1,
                    lineageCommittedAtEpochMs:
                      typeof value.lineageCommittedAtEpochMs === "number"
                        ? value.lineageCommittedAtEpochMs
                        : -1,
                    claimedAtEpochMs:
                      typeof value.claimedAtEpochMs === "number"
                        ? value.claimedAtEpochMs
                        : -1,
                  })
                : createSpecialistLocationGatewayNavigationOwnerKey({
                    phase: "pre-draft",
                    ...body,
                  });
  if (value.ownerKey !== expectedOwnerKey) {
    return { valid: false, reason: "specialist-location-gateway-authority-owner-mismatch" };
  }

  if (
    value.kind === "specialist-location-gateway-pre-draft-resolution" &&
    value.mutationRevision === 0
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 0,
        ...body,
        ownerKey: value.ownerKey,
      },
    };
  }

  if (
    value.kind ===
      "specialist-location-gateway-catalog-transition-claim" &&
    value.mutationRevision === 3 &&
    isSpecialistLocationGatewayIdentifier(value.attemptId) &&
    (value.targetCatalog === "packages" ||
      value.targetCatalog === "services") &&
    Number.isSafeInteger(value.targetGeneration) &&
    Number(value.targetGeneration) === Number(value.generation) + 1 &&
    (value.targetCatalog === "packages") ===
      (selection.authority.kind === "appointment") &&
    isEpoch(value.claimedAtEpochMs) &&
    value.claimedAtEpochMs >= Number(value.createdAtEpochMs) &&
    value.claimedAtEpochMs < Number(value.expiresAtEpochMs)
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 3,
        ...body,
        ownerKey: value.ownerKey,
        attemptId: value.attemptId,
        targetCatalog: value.targetCatalog,
        targetGeneration: value.targetGeneration as number,
        claimedAtEpochMs: value.claimedAtEpochMs,
      },
    };
  }

  if (
    value.kind === "specialist-location-gateway-auth-rebind-claim" &&
    value.mutationRevision === 4 &&
    value.customerAccountId === null &&
    isSpecialistLocationGatewayIdentifier(value.attemptId) &&
    isSpecialistLocationGatewayIdentifier(
      value.authenticatedCustomerAccountId,
    ) &&
    Number.isSafeInteger(value.targetGeneration) &&
    Number(value.targetGeneration) === Number(value.generation) + 1 &&
    isEpoch(value.targetCreatedAtEpochMs) &&
    isEpoch(value.targetExpiresAtEpochMs) &&
    isEpoch(value.claimedAtEpochMs) &&
    value.targetCreatedAtEpochMs >= Number(value.createdAtEpochMs) &&
    value.targetCreatedAtEpochMs <= value.claimedAtEpochMs &&
    value.claimedAtEpochMs >= Number(value.createdAtEpochMs) &&
    value.claimedAtEpochMs < Number(value.expiresAtEpochMs) &&
    value.targetExpiresAtEpochMs > value.targetCreatedAtEpochMs &&
    value.targetExpiresAtEpochMs <= Number(value.expiresAtEpochMs)
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 4,
        ...body,
        ownerKey: value.ownerKey,
        attemptId: value.attemptId,
        authenticatedCustomerAccountId:
          value.authenticatedCustomerAccountId,
        targetGeneration: value.targetGeneration as number,
        targetCreatedAtEpochMs: value.targetCreatedAtEpochMs,
        targetExpiresAtEpochMs: value.targetExpiresAtEpochMs,
        claimedAtEpochMs: value.claimedAtEpochMs,
      },
    };
  }

  if (
    value.kind ===
      "specialist-location-gateway-destination-lifecycle-claim" &&
    value.mutationRevision === 5 &&
    value.resolutionId === value.draftId &&
    isSpecialistLocationGatewayIdentifier(value.lineageAttemptId) &&
    isSpecialistLocationGatewayIdentifier(value.lifecycleAttemptId) &&
    (value.action === "close" ||
      value.action === "return-to-pre-draft") &&
    (value.destinationKind === "individual" ||
      value.destinationKind === "group" ||
      value.destinationKind === "package") &&
    (value.destinationKind === "package") ===
      (selection.authority.kind === "package") &&
    (value.destinationKind !== "group" ||
      (selection.authority.kind === "appointment" &&
        selection.authority.groupCapability)) &&
    (value.destinationKind !== "individual" ||
      (selection.authority.kind === "appointment" &&
        selection.authority.individualCapability)) &&
    isSpecialistLocationGatewayIdentifier(value.destinationDraftId) &&
    isSpecialistLocationGatewayFingerprint(
      value.destinationDraftFingerprint,
    ) &&
    isSpecialistLocationGatewayFingerprint(value.mutableCleanupFingerprint) &&
    isEpoch(value.lineageCommittedAtEpochMs) &&
    isEpoch(value.claimedAtEpochMs) &&
    value.lineageCommittedAtEpochMs >= Number(value.createdAtEpochMs) &&
    // Once the exact destination exists, its immutable fingerprint and the
    // mutable aggregate inspection—not the short pre-draft TTL—authorize a
    // serialized Back/Close lifecycle claim.
    value.claimedAtEpochMs >= value.lineageCommittedAtEpochMs
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 5,
        ...body,
        ownerKey: value.ownerKey,
        resolutionId: value.resolutionId,
        lineageAttemptId: value.lineageAttemptId,
        lifecycleAttemptId: value.lifecycleAttemptId,
        action: value.action,
        destinationKind: value.destinationKind,
        destinationDraftId: value.destinationDraftId,
        destinationDraftFingerprint: value.destinationDraftFingerprint,
        mutableCleanupFingerprint: value.mutableCleanupFingerprint,
        lineageCommittedAtEpochMs: value.lineageCommittedAtEpochMs,
        claimedAtEpochMs: value.claimedAtEpochMs,
      },
    };
  }

  if (
    value.kind ===
      "specialist-location-gateway-committed-auth-rebind-claim" &&
    value.mutationRevision === 6 &&
    value.customerAccountId === null &&
    value.resolutionId === value.draftId &&
    isSpecialistLocationGatewayIdentifier(value.lineageAttemptId) &&
    isSpecialistLocationGatewayIdentifier(value.authAttemptId) &&
    (value.destinationKind === "individual" ||
      value.destinationKind === "group" ||
      value.destinationKind === "package") &&
    (value.destinationKind === "package") ===
      (selection.authority.kind === "package") &&
    (value.destinationKind !== "group" ||
      (selection.authority.kind === "appointment" &&
        selection.authority.groupCapability)) &&
    (value.destinationKind !== "individual" ||
      (selection.authority.kind === "appointment" &&
        selection.authority.individualCapability)) &&
    isSpecialistLocationGatewayIdentifier(value.destinationDraftId) &&
    isSpecialistLocationGatewayFingerprint(
      value.priorDestinationDraftFingerprint,
    ) &&
    isSpecialistLocationGatewayFingerprint(
      value.authenticatedDestinationDraftFingerprint,
    ) &&
    (value.destinationOwnerBindingMode === "companion-only" ||
      value.destinationOwnerBindingMode === "durable-owner") &&
    (value.destinationOwnerBindingMode === "companion-only"
      ? value.authenticatedDestinationDraftFingerprint ===
        value.priorDestinationDraftFingerprint
      : value.authenticatedDestinationDraftFingerprint !==
        value.priorDestinationDraftFingerprint) &&
    isSpecialistLocationGatewayIdentifier(
      value.authenticatedCustomerAccountId,
    ) &&
    Number.isSafeInteger(value.targetGeneration) &&
    Number(value.targetGeneration) === Number(value.generation) + 1 &&
    isEpoch(value.targetCreatedAtEpochMs) &&
    isEpoch(value.targetExpiresAtEpochMs) &&
    isEpoch(value.lineageCommittedAtEpochMs) &&
    isEpoch(value.claimedAtEpochMs) &&
    value.lineageCommittedAtEpochMs >= Number(value.createdAtEpochMs) &&
    value.targetCreatedAtEpochMs >= value.lineageCommittedAtEpochMs &&
    value.targetCreatedAtEpochMs <= value.claimedAtEpochMs &&
    value.claimedAtEpochMs < Number(value.expiresAtEpochMs) &&
    value.targetExpiresAtEpochMs > value.targetCreatedAtEpochMs &&
    value.targetExpiresAtEpochMs <= Number(value.expiresAtEpochMs)
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 6,
        ...body,
        ownerKey: value.ownerKey,
        resolutionId: value.resolutionId,
        lineageAttemptId: value.lineageAttemptId,
        authAttemptId: value.authAttemptId,
        destinationKind: value.destinationKind,
        destinationDraftId: value.destinationDraftId,
        priorDestinationDraftFingerprint:
          value.priorDestinationDraftFingerprint,
        authenticatedDestinationDraftFingerprint:
          value.authenticatedDestinationDraftFingerprint,
        destinationOwnerBindingMode: value.destinationOwnerBindingMode,
        authenticatedCustomerAccountId:
          value.authenticatedCustomerAccountId,
        targetGeneration: value.targetGeneration as number,
        targetCreatedAtEpochMs: value.targetCreatedAtEpochMs,
        targetExpiresAtEpochMs: value.targetExpiresAtEpochMs,
        lineageCommittedAtEpochMs: value.lineageCommittedAtEpochMs,
        claimedAtEpochMs: value.claimedAtEpochMs,
      },
    };
  }

  const destinationIsAuthorized =
    (value.destinationKind === "package") ===
      (selection.authority.kind === "package") &&
    (value.destinationKind !== "group" ||
      (selection.authority.kind === "appointment" &&
        selection.authority.groupCapability)) &&
    (value.destinationKind !== "individual" ||
      (selection.authority.kind === "appointment" &&
        selection.authority.individualCapability));

  if (
    value.kind === "specialist-location-gateway-navigation-commit-claim" &&
    value.mutationRevision === 1 &&
    isSpecialistLocationGatewayIdentifier(value.attemptId) &&
    (value.destinationKind === "individual" ||
      value.destinationKind === "group" ||
      value.destinationKind === "package") &&
    isSpecialistLocationGatewayIdentifier(value.destinationDraftId) &&
    isSpecialistLocationGatewayFingerprint(value.destinationDraftFingerprint) &&
    isEpoch(value.claimedAtEpochMs) &&
    value.claimedAtEpochMs >= Number(value.createdAtEpochMs) &&
    value.claimedAtEpochMs < Number(value.expiresAtEpochMs) &&
    destinationIsAuthorized
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 1,
        ...body,
        ownerKey: value.ownerKey,
        attemptId: value.attemptId,
        destinationKind: value.destinationKind,
        destinationDraftId: value.destinationDraftId,
        destinationDraftFingerprint: value.destinationDraftFingerprint,
        claimedAtEpochMs: value.claimedAtEpochMs,
      },
    };
  }

  if (
    value.kind === "specialist-location-gateway-navigation-lineage" &&
    value.mutationRevision === 2 &&
    value.resolutionId === value.draftId &&
    isSpecialistLocationGatewayIdentifier(value.attemptId) &&
    (value.destinationKind === "individual" ||
      value.destinationKind === "group" ||
      value.destinationKind === "package") &&
    isSpecialistLocationGatewayIdentifier(value.destinationDraftId) &&
    isSpecialistLocationGatewayFingerprint(value.destinationDraftFingerprint) &&
    isEpoch(value.committedAtEpochMs) &&
    value.committedAtEpochMs >= Number(value.createdAtEpochMs) &&
    value.committedAtEpochMs < Number(value.expiresAtEpochMs) &&
    destinationIsAuthorized
  ) {
    return {
      valid: true,
      draft: {
        schemaVersion: SPECIALIST_LOCATION_GATEWAY_SCHEMA_VERSION,
        kind: value.kind,
        mutationRevision: 2,
        ...body,
        ownerKey: value.ownerKey,
        resolutionId: value.resolutionId,
        attemptId: value.attemptId,
        destinationKind: value.destinationKind,
        destinationDraftId: value.destinationDraftId,
        destinationDraftFingerprint: value.destinationDraftFingerprint,
        committedAtEpochMs: value.committedAtEpochMs,
      },
    };
  }

  return { valid: false, reason: "invalid-specialist-location-gateway-authority" };
}

export function isSpecialistLocationGatewayPublicationRowCount(
  value: unknown,
): value is readonly unknown[] {
  return Array.isArray(value) && value.length <= MAX_PUBLICATION_ROWS;
}

export const specialistLocationGatewayContractInternals = {
  cloneCatalogReference,
  clonePackageMapping,
  cloneServiceMapping,
  isEpoch,
  isLocale,
  isRecord,
};
